/**
 * 图片上传组件 (ImageUploader)
 * 
 * 功能：
 * - 多图上传（支持数量限制）
 * - 实时上传进度显示
 * - 精美紧凑的缩略图网格预览 (带删除、放大预览)
 * - 智能兼容 Array、JSON 字符串、单 URL 格式
 * - 自动压缩图片
 * - 自动通过 Nginx /storage/ 代理中转
 */

import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { X, Plus, ImageIcon, Eye } from 'lucide-react';
import presignedUploadService from '../../services/presignedUploadService';
import { convertToProxyUrl } from '../../utils/imageUrl';
import ImagePreviewModal from './ImagePreviewModal';

/**
 * 统一解析图片 URL 列表（支持数组、JSON 字符串、逗号分隔等）
 */
const parseImageUrls = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val.filter(Boolean);
  if (typeof val === 'string') {
    const trimmed = val.trim();
    if (!trimmed || trimmed === '[]') return [];
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return parsed.filter(Boolean);
      } catch {
        // fallback
      }
    }
    return [trimmed];
  }
  return [];
};

const ImageUploader = ({
  value = [],
  onChange,
  maxFiles = 5,
  disabled = false,
  uploadMeta = {},
  onSuccess,
  onError,
  accept = 'image/*'
}) => {
  const fileInputRef = useRef(null);
  const [currentUrls, setCurrentUrls] = useState(() => parseImageUrls(value));
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [currentUploadIndex, setCurrentUploadIndex] = useState(0);
  const [totalUploadCount, setTotalUploadCount] = useState(0);
  const [previewModalUrl, setPreviewModalUrl] = useState(null);

  // 同步外部 value 到内部 currentUrls
  useEffect(() => {
    setCurrentUrls(parseImageUrls(value));
  }, [value]);

  /**
   * 打开文件选择对话框
   */
  const handleSelectFiles = () => {
    if (fileInputRef.current && !disabled && !isUploading) {
      fileInputRef.current.click();
    }
  };

  /**
   * 处理文件选择与批量上传
   */
  const handleFileChange = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const filesToUpload = Array.from(files);
    console.log(`[ImageUploader] 准备上传 ${filesToUpload.length} 个文件`);

    // 检查数量限制
    if (maxFiles > 0 && currentUrls.length + filesToUpload.length > maxFiles) {
      const errorMsg = `最多只能选择 ${maxFiles} 张图片`;
      setUploadError(errorMsg);
      if (onError) onError(errorMsg);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // 检查必需元数据
    if (!uploadMeta.imageType) {
      const errorMsg = '缺少图片类型配置';
      setUploadError(errorMsg);
      if (onError) onError(errorMsg);
      return;
    }

    // 开始上传流程
    setIsUploading(true);
    setUploadError(null);
    setUploadProgress(0);
    setCurrentUploadIndex(0);
    setTotalUploadCount(filesToUpload.length);

    const uploadedUrlsInThisBatch = [];
    let hasError = false;

    for (let i = 0; i < filesToUpload.length; i++) {
      const file = filesToUpload[i];
      setCurrentUploadIndex(i + 1);
      
      const progress = Math.round((i / filesToUpload.length) * 100);
      setUploadProgress(progress);

      try {
        if (i > 0) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }

        const result = await presignedUploadService.uploadFile(
          file,
          uploadMeta.imageType,
          uploadMeta.studentId,
          uploadMeta.studentName,
          uploadMeta.courseId,
          uploadMeta.courseName,
          uploadMeta.teacherName
        );

        if (result.success && result.url) {
          uploadedUrlsInThisBatch.push(result.url);
        } else {
          console.error(`[ImageUploader] 第 ${i + 1} 个文件上传失败:`, result.error);
          const errorMsg = result.error || '上传失败';
          setUploadError(`文件 "${file.name}" 上传失败: ${errorMsg}`);
          if (onError) onError(errorMsg);
          hasError = true;
        }
      } catch (err) {
        console.error(`[ImageUploader] 上传过程发生异常:`, err);
        setUploadError(`上传异常: ${err.message}`);
        if (onError) onError(err.message);
        hasError = true;
      }
    }

    // 更新总进度到 100%
    setUploadProgress(100);
    setTimeout(() => {
      setIsUploading(false);
    }, 400);

    // 如果有成功上传的，更新外部状态
    if (uploadedUrlsInThisBatch.length > 0) {
      const newUrls = [...currentUrls, ...uploadedUrlsInThisBatch];
      setCurrentUrls(newUrls);
      if (onChange) onChange(newUrls);
      if (onSuccess) onSuccess(uploadedUrlsInThisBatch);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /**
   * 删除图片
   */
  const handleRemoveImage = async (index) => {
    const urlToRemove = currentUrls[index];
    const newUrls = currentUrls.filter((_, i) => i !== index);
    setCurrentUrls(newUrls);
    if (onChange) onChange(newUrls);

    try {
      await presignedUploadService.deleteFile(urlToRemove);
    } catch (error) {
      console.error('删除云端文件失败:', error);
    }
  };

  const canUpload = !disabled && !isUploading && (maxFiles === 0 || currentUrls.length < maxFiles);

  return (
    <div className="image-uploader w-full space-y-3">
      {/* 隐藏的文件输入 */}
      <input
        ref={fileInputRef}
        type="file"
        multiple={maxFiles !== 1}
        accept={accept}
        className="hidden"
        onChange={handleFileChange}
      />

      {/* 上传进度条 */}
      {isUploading && (
        <div className="p-3 bg-blue-50/80 backdrop-blur rounded-xl border border-blue-200/80 shadow-sm animate-pulse">
          <div className="flex items-center justify-between text-xs text-blue-700 font-medium mb-1.5">
            <div className="flex items-center gap-2">
              <div className="w-3.5 h-3.5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <span>正在上传第 {currentUploadIndex} / {totalUploadCount} 张图片...</span>
            </div>
            <span>{uploadProgress}%</span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-blue-100 overflow-hidden">
            <div
              className="h-1.5 rounded-full bg-blue-600 transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* 错误提示 */}
      {uploadError && (
        <div className="p-2.5 bg-red-50 rounded-xl border border-red-200">
          <p className="text-xs text-red-600 font-medium">{uploadError}</p>
        </div>
      )}

      {/* 图片网格与添加按钮 */}
      {currentUrls.length > 0 ? (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
          {currentUrls.map((rawUrl, index) => {
            const displayUrl = convertToProxyUrl(rawUrl);
            return (
              <div
                key={index}
                className="group relative aspect-square rounded-xl overflow-hidden bg-gray-100 border border-gray-200 shadow-sm cursor-pointer hover:shadow-md transition-all"
                onClick={() => setPreviewModalUrl(displayUrl)}
              >
                <img
                  src={displayUrl}
                  alt={`图片 ${index + 1}`}
                  className="w-full h-full object-cover"
                  loading="eager"
                  onError={(e) => {
                    console.error('图片缩略图加载失败:', displayUrl);
                  }}
                />
                
                {/* 悬停放大镜指示 */}
                <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                  <Eye size={20} className="text-white" />
                </div>

                {/* 徽标 */}
                <div className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded bg-black/60 backdrop-blur-sm text-[10px] text-white font-medium">
                  {uploadMeta.imageType || '图片'}
                </div>

                {/* 删除按钮 */}
                {!disabled && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveImage(index);
                    }}
                    className="absolute top-1 right-1 p-1 bg-red-500/90 hover:bg-red-600 active:scale-95 text-white rounded-full shadow transition-all"
                    title="删除"
                  >
                    <X size={13} />
                  </button>
                )}
              </div>
            );
          })}

          {/* 还有剩余配额时显示 + 按钮卡片 */}
          {canUpload && (
            <button
              type="button"
              onClick={handleSelectFiles}
              className="aspect-square rounded-xl border-2 border-dashed border-gray-300 hover:border-blue-500 hover:bg-blue-50/50 active:scale-95 flex flex-col items-center justify-center text-gray-400 hover:text-blue-600 transition-all group"
            >
              <Plus size={24} className="mb-1 group-hover:scale-110 transition-transform" />
              <span className="text-xs font-medium">添加</span>
            </button>
          )}
        </div>
      ) : (
        /* 空状态卡片 */
        !isUploading && (
          <div
            className={`p-6 border-2 border-dashed rounded-2xl text-center transition-all ${
              canUpload
                ? 'border-gray-300 bg-gray-50/60 hover:border-blue-400 hover:bg-blue-50/40 cursor-pointer active:scale-[0.99]'
                : 'border-gray-200 bg-gray-50/30 cursor-not-allowed'
            }`}
            onClick={canUpload ? handleSelectFiles : undefined}
          >
            <div className="w-12 h-12 mx-auto mb-2.5 rounded-full bg-blue-50 flex items-center justify-center text-blue-500">
              <ImageIcon size={22} />
            </div>
            {canUpload ? (
              <>
                <p className="text-sm font-semibold text-gray-700 mb-0.5">点击上传{uploadMeta.imageType || '图片'}</p>
                <p className="text-xs text-gray-400">
                  支持 JPG / PNG，最多 {maxFiles === 0 ? '不限' : `${maxFiles} 张`}
                </p>
              </>
            ) : (
              <p className="text-sm text-gray-400">
                {disabled ? '暂无凭证' : '已达到最大上传数量'}
              </p>
            )}
          </div>
        )
      )}

      {/* 大图预览弹窗 */}
      {previewModalUrl && (
        <ImagePreviewModal
          imageUrl={previewModalUrl}
          altText={uploadMeta.imageType || '图片预览'}
          onClose={() => setPreviewModalUrl(null)}
        />
      )}
    </div>
  );
};

ImageUploader.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.string
  ]),
  onChange: PropTypes.func,
  maxFiles: PropTypes.number,
  disabled: PropTypes.bool,
  uploadMeta: PropTypes.shape({
    imageType: PropTypes.string.isRequired,
    studentId: PropTypes.string,
    studentName: PropTypes.string,
    courseId: PropTypes.string,
    courseName: PropTypes.string,
    teacherName: PropTypes.string
  }),
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
  accept: PropTypes.string
};

export default ImageUploader;
